import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Upload, Download, X, Search, FileSpreadsheet, AlertTriangle, CheckCircle2, ChevronRight } from "lucide-react";

export default function Customers() {
  const { can } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [imp, setImp] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/customers${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    setRows(data);
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-head text-2xl font-extrabold">Customers</h1>
        <div className="flex items-center gap-2">
          {can("create") && <button onClick={() => setImp(true)} data-testid="import-links" className="h-9 px-4 rounded-lg border border-slate-300 text-sm font-semibold flex items-center gap-2 hover:bg-slate-50"><Upload size={16} /> Import sheet</button>}
          {can("create") && <button onClick={() => nav("/customer-link")} data-testid="add-link" className="h-9 px-4 rounded-lg bg-[var(--brand)] text-white text-sm font-semibold flex items-center gap-2 hover:bg-[var(--brand-hover)]"><Plus size={16} /> Add Customer Link</button>}
        </div>
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers…" data-testid="cust-search"
          className="w-full pl-9 h-10 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b text-left text-[11px] uppercase text-slate-500">
            <th className="px-4 py-3">Customer</th><th className="px-4 py-3">Code</th>
            <th className="px-4 py-3">Account manager</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} onClick={() => nav(`/customers/${c.id}`)} data-testid={`cust-row-${c.id}`}
                className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
                <td className="px-4 py-3 font-semibold">{c.name}</td>
                <td className="px-4 py-3 text-slate-500">{c.code || "—"}</td>
                <td className="px-4 py-3">{c.account_manager || "—"}</td>
                <td className="px-4 py-3">{c.status || "Active"}</td>
                <td className="px-4 py-3 text-right text-slate-400"><ChevronRight size={16} /></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No customers.</td></tr>}
          </tbody>
        </table>
      </div>

      {imp && <ImportLinksModal onClose={() => setImp(false)} onDone={() => { setImp(false); load(); }} />}
    </div>
  );
}

function ImportLinksModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const template = async () => {
    try {
      const r = await api.get("/import/customer-links/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = "nextra_import_customer_links.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download template"); }
  };
  const onPick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFile(f); setBusy(true); setPreview(null);
    try { const fd = new FormData(); fd.append("file", f); const r = await api.post("/import/customer-links/preview", fd); setPreview(r.data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const r = await api.post("/import/customer-links/commit", fd); toast.success(`Imported ${r.data.links} links (${r.data.new_customers} new customers, ${r.data.new_vendors} new vendors)`); onDone(); }
    catch (e) {
      const d = e.response?.data?.detail;
      if (d?.errors) { setPreview({ ok: false, errors: d.errors, summary: preview?.summary }); toast.error(d.message || "Import rejected"); }
      else toast.error(apiError(d));
    } finally { setBusy(false); }
  };

  const s = preview?.summary;
  const matched = s ? [...(s.matched_customers || []), ...(s.matched_vendors || [])] : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="import-links-modal">
        <div className="flex items-center justify-between p-5 border-b"><h3 className="font-head font-bold text-lg flex items-center gap-2"><FileSpreadsheet size={18} className="text-[var(--brand)]" /> Import customer links (one sheet)</h3><button onClick={onClose} className="text-slate-400"><X /></button></div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <p className="text-sm text-slate-600">One row per link. Customers &amp; vendors are matched by name or created. You’ll see a preview before anything is saved.</p>
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={template} className="h-9 px-4 rounded-lg border border-slate-300 text-sm font-semibold flex items-center gap-2 hover:bg-slate-50"><Download size={16} /> Download template</button>
            <label className="h-9 px-4 rounded-lg bg-[var(--brand)] text-white text-sm font-semibold flex items-center gap-2 cursor-pointer"><Upload size={16} /> Choose file<input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onPick} data-testid="import-links-file" /></label>
            {file && <span className="text-sm text-slate-500 truncate">{file.name}</span>}
          </div>
          {busy && <div className="text-sm text-slate-500">Checking…</div>}

          {preview && preview.ok && s && (
            <div className="space-y-3" data-testid="links-preview">
              <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm">
                <CheckCircle2 size={18} /> Ready: will create <b>{s.new_customers}</b> new customers, <b>{s.new_vendors}</b> new vendors, <b>{s.new_sites}</b> sites, <b>{s.links}</b> links.
              </div>
              {matched.length > 0 && (
                <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 text-sm">
                  <div className="font-semibold text-amber-800 mb-1">Matched to existing by similarity — confirm these are the same:</div>
                  <ul className="list-disc list-inside text-amber-900">
                    {matched.map((m, i) => <li key={i}>“{m.typed}” → <b>{m.matched}</b> ({Math.round(m.score * 100)}%)</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          {preview && !preview.ok && (
            <div className="space-y-2" data-testid="links-errors">
              <div className="flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm"><AlertTriangle size={18} /> {preview.errors.length} problem(s). Nothing was imported.</div>
              <div className="border border-slate-200 rounded-lg overflow-auto max-h-64">
                <table className="w-full text-sm"><thead><tr className="bg-slate-50 border-b text-left text-[11px] uppercase text-slate-500"><th className="px-3 py-2 w-14">Row</th><th className="px-3 py-2">Column</th><th className="px-3 py-2">Problem</th></tr></thead>
                  <tbody>{preview.errors.map((er, i) => <tr key={i} className="border-b border-slate-100"><td className="px-3 py-2 font-mono-num">{er.row}</td><td className="px-3 py-2">{er.field}</td><td className="px-3 py-2 text-red-600">{er.problem}</td></tr>)}</tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <div className="p-5 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 h-10 rounded-lg border text-sm">Close</button>
          <button onClick={commit} disabled={busy || !preview?.ok} data-testid="links-commit" className="px-5 h-10 rounded-lg bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50">Confirm &amp; import</button>
        </div>
      </div>
    </div>
  );
}
